import {
    Button,
    Stack,
    TextInput,
    Title,
} from '@mantine/core';
import {StaffService, type app__schema__staff__Add} from '../../client';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {DataTable, DataTableSortStatus} from 'mantine-datatable';
import sortBy from 'lodash/sortBy';
import {useEffect, useState} from 'react';
import {closeModal, openModal} from '@mantine/modals';
import {notifications} from '@mantine/notifications';
import {PageLayout} from '../shell/PageLayout';
import {DeleteTableAction} from '../common/TableActionIcons';
import {useDataTablePagination} from '../../utils/useDataTablePagination';

export type StaffMember = {
    id: number;
    email: string;
    firstname: string;
    lastname: string;
};

type StaffListResponse = {
    staff_members: StaffMember[];
};

export const Staff = () => {
    const {isLoading, data} = useQuery({
        queryKey: ['staff'],
        queryFn: async () => {
            const response = await StaffService.getAllStaffApiV1StaffGet();
            return response as StaffListResponse;
        },
    });

    const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
    const queryClient = useQueryClient();

    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<StaffMember>>({
        columnAccessor: 'firstname',
        direction: 'asc',
    });

    const {page, recordsPerPage, paginate, getPaginationProps} = useDataTablePagination();
    const [records, setRecords] = useState<StaffMember[]>([]);

    useEffect(() => {
        const sorted = sortBy(data?.staff_members ?? [], sortStatus.columnAccessor) as StaffMember[];
        const result = sortStatus.direction === 'desc' ? [...sorted].reverse() : sorted;
        setStaffMembers(result);
        setRecords(paginate(result));
    }, [data, sortStatus, page, recordsPerPage, paginate]);

    const deleteStaffMember = (member: StaffMember) => {
        StaffService.deleteStaffApiV1StaffStaffIdDelete({staffId: member.id})
            .then(() => {
                queryClient.invalidateQueries({queryKey: ['staff']});
                closeModal('deleteStaff');
            })
            .catch((error: { body?: { detail?: string }; message?: string }) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not delete staff member',
                    message: error.body?.detail ?? error.message ?? 'Request failed',
                });
            });
    };

    const addStaffMember = (member: app__schema__staff__Add) => {
        if (!member.email || !member.firstname || !member.lastname) {
            return;
        }
        StaffService.addStaffApiV1StaffPost({requestBody: member})
            .then(() => {
                queryClient.invalidateQueries({queryKey: ['staff']});
                queryClient.invalidateQueries({queryKey: ['registrations']});
                closeModal('addStaff');
            })
            .catch((error: { body?: { detail?: string }; message?: string }) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not add staff member',
                    message: error.body?.detail ?? error.message ?? 'Request failed',
                });
            });
    };

    const showAddModal = () => {
        const member: app__schema__staff__Add = {email: '', firstname: '', lastname: ''};
        openModal({
            modalId: 'addStaff',
            title: 'Add staff member',
            children: (
                <>
                    <TextInput
                        label="First name"
                        required
                        placeholder="First name"
                        onChange={(e) => {
                            member.firstname = e.target.value;
                        }}
                    />
                    <TextInput
                        label="Last name"
                        required
                        placeholder="Last name"
                        onChange={(e) => {
                            member.lastname = e.target.value;
                        }}
                    />
                    <TextInput
                        label="E-mail"
                        required
                        placeholder="e@mail.com"
                        onChange={(e) => {
                            member.email = e.target.value;
                        }}
                    />
                    <Button type="submit" variant="outline" mt="xs" onClick={() => addStaffMember(member)}>
                        Add
                    </Button>
                </>
            ),
        });
    };

    const showDeleteModal = (member: StaffMember) => {
        openModal({
            modalId: 'deleteStaff',
            title: 'Delete staff member',
            children: (
                <Stack gap="xs">
                    <p>
                        Remove <strong>{member.firstname} {member.lastname}</strong> ({member.email}) from the staff list?
                    </p>
                    <Button color="red" onClick={() => deleteStaffMember(member)}>Yes, delete</Button>
                    <Button onClick={() => closeModal('deleteStaff')}>Cancel</Button>
                </Stack>
            ),
        });
    };

    return (
        <PageLayout
            title="Staff"
            description="Staff members registered for the camp."
            actions={(
                <Button variant="outline" onClick={showAddModal}>
                    Add staff member
                </Button>
            )}
        >
            <DataTable
                withTableBorder
                striped
                highlightOnHover
                records={records}
                fetching={isLoading}
                loaderType="oval"
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                {...getPaginationProps(staffMembers.length)}
                columns={[
                    {accessor: 'firstname', title: 'First name', sortable: true},
                    {accessor: 'lastname', title: 'Last name', sortable: true},
                    {
                        accessor: 'email',
                        title: 'Email',
                        render: ({email}) => <a href={`mailto:${email}`}>{email}</a>,
                    },
                    {
                        accessor: 'delete',
                        title: '',
                        render: (record) => (
                            <DeleteTableAction
                                onClick={() => showDeleteModal(record)}
                                label="Delete staff member"
                            />
                        ),
                    },
                ]}
            />
        </PageLayout>
    );
};
